import React, { useEffect, useMemo, useState } from 'react';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Dialog,
  DialogContent,
  MenuItem,
  Box,
  TextField,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import Button from '../../components/styled_api/StyledButton';
import { createOrganization } from '../../api/orgService';
import { useAuth } from '../../contexts/AuthContext';
import { getUsers } from '../../api/userService';
import {
  isActiveUser,
  MARKETSMART_ORG_ID,
} from '../../utilities/superadmin';
import { captureHandledError } from '../../utilities/datadog';
import type { EnrichedUser } from '../../types/auth';

interface CreateOrganizationDialogProps {
  isOpen: boolean;
  creationType: string;
  onClose: () => void;
}

type Field =
  | 'crm'
  | 'csm_id'
  | 'name'
  | 'sector'
  | 'fiscal_year_start'
  | 'domain'
  | 'major_gift_threshold'
  | 'average_major_gift'
  | 'average_legacy_gift';

type FieldValue = string | number | null;

type CsmSelectOption = { label: string; value: string };

type CreateOrgPayloadValue = string | number | boolean | null;

type FormChangeEvent =
  | SelectChangeEvent<string>
  | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

const CreateOrganizationDialog: React.FC<CreateOrganizationDialogProps> = ({
  isOpen,
  creationType,
  onClose,
}) => {
  // #region State Variables
  const [name, setOrgName] = useState<string | null>(null);
  const [sector, setSector] = useState<string | null>(null);
  const [fiscal_year_start, setFiscalYear] = useState<string | null>(null);
  const [crm, setCRM] = useState<string | null>(null);
  // Store CSM as a string id so it matches the Select option values
  const [csm, setCSM] = useState<string | null>(null);
  const [domain, setDomain] = useState<string | null>(null);
  const [major_gift_threshold, setMajorGift] = useState<number | null>(null);
  const [average_major_gift, setAverageMajorGift] = useState<number | null>(null);
  const [average_legacy_gift, setAverageLegacyGift] = useState<number | null>(null);
  const [complete, setComplete] = useState(false);
  const [csmData, setCSMData] = useState<CsmSelectOption[]>([]);
  // #endregion

  // #region Constants
  const { changeOrganization } = useAuth();

  const MonthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const OrganizationSector = {
    ARTS_CULTURE_HUMANITIES: 'Arts, Culture, Humanities',
    EDUCATION: 'Education',
    ENVIRONMENT_AND_ANIMALS: 'Environment and Animals',
    HEALTH: 'Health',
    HUMAN_SERVICES: 'Human Services',
    INTERNATIONAL: 'International',
    PUBLIC_SOCIETY_BENEFIT: 'Public, Society Benefit',
    RELIGION: 'Religion',
    OTHER: 'Other',
  };

  const CRMSolutions = {
    SALESFORCE: 'Salesforce',
    BLACKBAUD: 'Blackbaud',
    BLOOMERANG: 'Bloomerang',
    LGL: 'Little Green Light',
    DONORBOX: 'Donorbox',
    NEONCRM: 'NeonCRM',
    KINDFUL: 'Kindful',
    DONORPERFECT: 'DonorPerfect',
    NFG: 'Network for Good',
    SUSTAIN: 'Sustain',
    HUBSPOT: 'HubSpot',
    MONDAY: 'Monday',
    NETSUITE: 'NetSuite',
    AGILE: 'AgileCRM',
    OTHER: 'Other',
  };

  const requiredFields = useMemo(
    () => [
      name,
      sector,
      fiscal_year_start,
      crm,
      domain,
      major_gift_threshold,
    ],
    [
      name,
      sector,
      fiscal_year_start,
      crm,
      domain,
      major_gift_threshold,
    ]
  );
  //   for used in dynamic handleChange
  const setters = {
    crm: setCRM,
    csm_id: setCSM,
    name: setOrgName,
    sector: setSector,
    fiscal_year_start: setFiscalYear,
    domain: setDomain,
    major_gift_threshold: setMajorGift,
    average_major_gift: setAverageMajorGift,
    average_legacy_gift: setAverageLegacyGift,
  } as Record<Field, React.Dispatch<React.SetStateAction<FieldValue>>>;

  // gather all fields on the form
  const allFields: Record<Field, FieldValue> = {
    name,
    sector,
    fiscal_year_start,
    crm,
    csm_id: csm,
    domain,
    major_gift_threshold,
    average_major_gift,
    average_legacy_gift
  };

  // helper function
  const isFilled = (v: unknown) => v !== null && v !== '' && v !== undefined;

  // create the payload for the createOrganization endpoint
  const toPayload = (
    includeNulls = false
  ): Record<string, CreateOrgPayloadValue> => {
    const out: Record<string, CreateOrgPayloadValue> = {};
    (Object.keys(allFields) as Field[]).forEach(k => {
      const v = allFields[k];

      if (includeNulls || isFilled(v)) out[k] = v;
    });
    // Convert fiscal_year_start month name to month number (1-12)
    if (typeof out['fiscal_year_start'] === 'string') {
      const idx = MonthNames.indexOf(out['fiscal_year_start']);
      if (idx >= 0) out['fiscal_year_start'] = idx + 1;
    }
    // Set isDemoOrg based on creationType
    out['demo_org'] = creationType === 'demo';
    return out;
  };
  // #endregion

  //   #region useEffect
  //   watch for changes of the required fields to toggle
  // the submit button
  useEffect(() => {
    setComplete(
      !requiredFields.some(
        (field: string | number | null) => field === null
      )
    );
  }, [requiredFields]);

  //   when we are in the demo version, we need to set
  // the required fields automatically
  useEffect(() => {
    const hash = Math.random().toString(36).substring(2, 12);
    const domainRandom = `temp.org_${hash}`;
    setFiscalYear(creationType === 'demo' ? 'January' : null);
    setSector(creationType === 'demo' ? 'Education' : null);
    setCRM(creationType === 'demo' ? 'Salesforce' : null);
    setDomain(creationType === 'demo' ? domainRandom : null);
    setMajorGift(creationType === 'demo' ? 10000 : null);
    setAverageLegacyGift(creationType === 'demo' ? 5000 : null);
    setAverageMajorGift(creationType === 'demo' ? 8000 : null);
  }, [creationType]);

  // MarketSmart org users only (when dialog is open); exclude deactivated staff
  useEffect(() => {
    if (!isOpen) return;

    const load = async () => {
      try {
        const resp = await getUsers(MARKETSMART_ORG_ID);
        if (!resp) return;

        const opts = resp.users
          .filter((u: EnrichedUser) => isActiveUser(u))
          .map((u: EnrichedUser) => ({
            label: u.email,
            value: String(u.id), // MUI Select values are strings
          }));

        setCSMData(opts);
      } catch (e) {
        captureHandledError(
          'Error fetching users for organization creation',
          e
        );
      }
    };

    load();
  }, [isOpen]);
  // #endregion

  //   #region Handle Functions
  const handleChange = (e: FormChangeEvent, type: Field) => {
    const raw = e.target.value;
    // different behavior for numeric inputs
    const isNumericField =
      type === 'major_gift_threshold' ||
      type === 'average_legacy_gift' ||
      type === 'average_major_gift';

    // treat empty UI value ('') as null in state
    const value =
      raw === ''
        ? null
        : isNumericField
        ? Number.isNaN(Number(raw))
          ? null
          : Number(raw)
        : raw;

    // setState variable for all form options here
    setters[type](value);
  };

  //   closing the modal
  const handleClose = () => {
    if (creationType === 'new') {
      setOrgName(null);
      setSector(null);
      setFiscalYear(null);
      setCRM(null);
      setCSM(null);
      setDomain(null);
      setMajorGift(null);
      setAverageMajorGift(null);
      setAverageLegacyGift(null);
    } else {
      setOrgName(null);
    }
    // call onClose from parent
    onClose?.();
  };

  // handle the form submit
  const handleFormSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    try {
      const payload = toPayload();
      const response = await createOrganization(payload);
      if (!response) return;

      changeOrganization(response.id);
      handleClose(); // close only after success

      // schedule reload in 5s
      await new Promise(r => setTimeout(r, 2000));
      window.location.reload();
    } catch (e) {
      captureHandledError('createOrganization failed', e);
    }
  };
  // #endregion

  //   #region Functions
  function typedEntries<T extends Record<string, string>>(obj: T) {
    return Object.entries(obj) as [keyof T, T[keyof T]][];
  }

  // #endregion

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth='xs'
      sx={{
        borderRadius: '28px',
        padding: '24px',
        boxShadow:
          '0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 3px 5px -1px rgba(0, 0, 0, 0.20)',
      }}
    >
      {/* Dialog Title Section */}

      <DialogTitle className='ms-h6'> Add your organization</DialogTitle>

      <DialogContent>
        {' '}
        <Box
          component='form'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 2,
            padding: '10px',
            '& .MuiTextField-root': { width: '100%' },
          }}
        >
          <TextField
            required
            value={name ?? ''}
            id='outlined-required'
            label='Organization Name'
            onChange={e => handleChange(e, 'name')}
          />

          {creationType === 'new' ? (
            <>
              <FormControl fullWidth required>
                <InputLabel>Sector</InputLabel>
                <Select
                  value={sector ?? ''}
                  label='Sector'
                  onChange={e => handleChange(e, 'sector')}
                >
                  {typedEntries(OrganizationSector).map(([key, label]) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Fiscal Year Start</InputLabel>
                <Select
                  value={fiscal_year_start ?? ''}
                  label='Fiscal Year Start'
                  onChange={e => handleChange(e, 'fiscal_year_start')}
                >
                  {MonthNames.map(m => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>CRM</InputLabel>
                <Select
                  value={crm ?? ''}
                  label='CRM'
                  onChange={e => handleChange(e, 'crm')}
                >
                  {typedEntries(CRMSolutions).map(([key, label]) => (
                    <MenuItem key={key} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>CSM</InputLabel>
                <Select
                  value={csm ?? ''}
                  label='CSM'
                  onChange={e => handleChange(e, 'csm_id')}
                >
                  <MenuItem value=''>
                    <em>None</em>
                  </MenuItem>
                  {csmData.map(item => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                value={domain ?? ''}
                required
                label='Domain'
                onChange={e => handleChange(e, 'domain')}
              />
              <TextField
                required
                type='number'
                value={major_gift_threshold ?? ''}
                label='Major Gift Threshold'
                onChange={e => handleChange(e, 'major_gift_threshold')}
              />
              <TextField
                required
                type='number'
                value={average_major_gift ?? ''}
                label='Average Major Gift'
                onChange={e => handleChange(e, 'average_major_gift')}
              />
              <TextField
                required
                type='number'
                value={average_legacy_gift ?? ''}
                label='Average Legacy Gift'
                onChange={e => handleChange(e, 'average_legacy_gift')}
              />
            </>
          ) : (
            <></>
          )}

          <Button
            className='button__primaryMain'
            disabled={!complete}
            sx={{ alignSelf: 'flex-end' }}
            label='Submit'
            width={'25%'}
            onClick={e => handleFormSubmit(e)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
